/**
 * Per-session playback resume.
 *
 * Remembers the last playhead UTC for each session in localStorage so
 * that re-opening or refreshing a session resumes where the viewer last
 * stopped, instead of restarting from the beginning (VOD) or snapping to
 * the live edge. Fed back into the player as `initialSeekUtcMs` on mount
 * (a lap-click seek still takes precedence). Best-effort: any storage
 * failure (quota, private mode) silently no-ops.
 */

const KEY = 'tv.viewer.resume.v1';
// Bound localStorage growth — keep the most-recently-saved N sessions.
const MAX_ENTRIES = 50;

interface ResumeEntry {
  utcMs: number;
  savedAt: number;
}

function readMap(): Record<string, ResumeEntry> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, ResumeEntry>) : {};
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, ResumeEntry>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // quota exceeded / storage disabled — resume is a convenience, not critical.
  }
}

/** Remember the playhead for a session. No-op on missing/invalid input. */
export function saveResumePosition(sessionId: string | null, utcMs: number | null): void {
  if (!sessionId || utcMs == null || !Number.isFinite(utcMs)) return;
  const map = readMap();
  map[sessionId] = { utcMs: Math.round(utcMs), savedAt: Date.now() };
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    // LRU eviction by save time.
    keys.sort((a, b) => map[a].savedAt - map[b].savedAt);
    for (const stale of keys.slice(0, keys.length - MAX_ENTRIES)) delete map[stale];
  }
  writeMap(map);
}

/** Last saved playhead UTC for a session, or null if none. */
export function loadResumePosition(sessionId: string | null): number | null {
  if (!sessionId) return null;
  const entry = readMap()[sessionId];
  return entry && Number.isFinite(entry.utcMs) ? entry.utcMs : null;
}

/** Forget a session's resume point (e.g. after an explicit restart). */
export function clearResumePosition(sessionId: string): void {
  const map = readMap();
  if (sessionId in map) {
    delete map[sessionId];
    writeMap(map);
  }
}
